'use client';

import React from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

let didInit = false;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    if (!POSTHOG_KEY) return;
    if (didInit) return;

    didInit = true;
    posthog.init(POSTHOG_KEY, {
      api_host: '/a',
      ui_host: 'https://eu.posthog.com',
      // Page tracking
      capture_pageview: true,
      capture_pageleave: true,
      // Session recording
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: false,
        // Block high-frequency DOM mutation sources to prevent
        // "Too many mutations" throttling in session replay.
        // Verified classes from @livekit/components-core prefixClass():
        //   lk-participant-media-video — <video> element with WebRTC stream (~60fps DOM changes)
        //   lk-audio-visualizer, lk-audio-bar-visualizer — SDK audio level bars
        // Our own components:
        //   .animate-ping — agent status indicator (continuous ripple)
        //   .animate-pulse — recording/duration indicators (continuous pulse)
        blockSelector: [
          '.lk-participant-media-video',
          '.lk-audio-visualizer',
          '.lk-audio-bar-visualizer',
          '.ph-no-capture',
          '[data-ph-no-capture]',
          '.animate-ping',
          '.animate-pulse',
        ].join(', '),
      },
      // Rename external scripts to bypass ad-blocker filename filters
      prepare_external_dependency_script: (script: HTMLScriptElement) => {
        script.src = script.src
          .replace('posthog-recorder.js', 'rec.js')
          .replace('recorder-v2.js', 'rec-v2.js')
          .replace('recorder.js', 'rec.js');
        return script;
      },
      // Console log capture (all levels for debugging)
      enable_recording_console_log: true,
      // Network performance recording - captures fetch/XHR in replay
      capture_performance: true,
      // Error tracking - autocapture exceptions
      autocapture: true,
      // Capture dead clicks (clicks that don't do anything)
      capture_dead_clicks: true,
    });
  }, []);

  // POSTHOG_KEY is a build-time constant, so the wrapper element type never
  // changes across renders — swapping it after init would remount the entire
  // app tree (and with it every active room connection).
  if (!POSTHOG_KEY) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

export function getPostHogSessionURL(): string | null {
  if (!POSTHOG_KEY) return null;
  try {
    return posthog.get_session_replay_url({ withTimestamp: true, timestampLookBack: 30 });
  } catch {
    return null;
  }
}

export function getPostHogSessionId(): string | null {
  if (!POSTHOG_KEY) return null;
  try {
    return posthog.get_session_id();
  } catch {
    return null;
  }
}

/**
 * Identify a user for cross-session tracking
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  try {
    posthog.identify(userId, properties);
  } catch {
    // Silent fail - analytics should never break the app
  }
}

/**
 * Set the room as a group for group analytics
 */
export function setRoomGroup(roomName: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  try {
    posthog.group('room', roomName, properties);
  } catch {
    // Silent fail
  }
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  try {
    posthog.capture(eventName, properties);
  } catch {
    // Silent fail
  }
}

/**
 * Capture an exception for error tracking
 */
export function captureException(error: Error, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  try {
    posthog.captureException(error, properties);
  } catch {
    // Silent fail
  }
}

/**
 * Reset user identity (call on logout/leave)
 */
export function resetUser() {
  if (!POSTHOG_KEY) return;
  try {
    posthog.reset();
  } catch {
    // Silent fail
  }
}
